import { useQuery } from "@tanstack/react-query";
import { Link, useParams, useSearch } from "@tanstack/react-router";
import type { Money, Order } from "@websede/contracts";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client.js";
import { Card, ErrorNotice } from "../../components/form.js";

function formatMoney(money: Money, locale: string): string {
  return new Intl.NumberFormat(locale, { style: "currency", currency: money.currency }).format(
    money.amountMinor / 100,
  );
}

const SETTLING: Order["status"][] = ["pending_payment", "paid"];

/** Where a purchase stands; polls while the payment or the registrations are still in flight. */
export function OrderPage() {
  const { t, i18n } = useTranslation();
  const { orderId } = useParams({ from: "/admin/orders/$orderId" });
  const search = useSearch({ from: "/admin/orders/$orderId" });
  const order = useQuery({
    queryKey: ["orders", orderId],
    queryFn: () => api.orders.get(orderId),
    refetchInterval: (query) =>
      query.state.data && SETTLING.includes(query.state.data.status) ? 2000 : false,
  });
  const data = order.data;
  const format = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(iso),
    );
  const tone =
    data?.status === "completed"
      ? "bg-good-soft text-good"
      : data?.status === "failed" || data?.status === "partially_failed"
        ? "bg-danger-soft text-danger"
        : "bg-accent-soft text-accent";

  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-4 md:p-8">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-[22px] text-ink">{t("order.title")}</h1>
          <p className="font-mono text-xs text-ink-3">{orderId}</p>
        </div>
        <Link
          to="/admin/domains"
          className="text-sm text-accent underline-offset-2 hover:underline"
        >
          {t("order.list")}
        </Link>
      </div>
      <ErrorNotice error={order.error} />
      {data ? (
        <>
          <div className={`rounded-md px-4 py-3 text-sm ${tone}`}>
            <div className="font-medium">{t(`order.status.${data.status}`)}</div>
            {data.status === "pending_payment" && search.cancelled ? (
              <div className="mt-1 text-xs">{t("order.cancelledNote")}</div>
            ) : null}
            {data.status === "pending_payment" && !search.cancelled ? (
              <div className="mt-1 text-xs">{t("order.waiting")}</div>
            ) : null}
            {data.status === "paid" ? (
              <div className="mt-1 text-xs">{t("order.waiting")}</div>
            ) : null}
          </div>
          <Card title={t("order.items")}>
            <ul className="divide-y divide-rule">
              {data.items.map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-4 py-3 text-sm">
                  <div className="min-w-0">
                    <div className="font-mono">{item.domain}</div>
                    <div className="text-xs text-ink-3">
                      {t("checkout.years", { count: item.years })} · {item.provider}
                      {item.expiresAt
                        ? ` · ${t("order.expires", { date: format(item.expiresAt) })}`
                        : ""}
                      {item.error ? ` · ${item.error}` : ""}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="tabular-nums">
                      {formatMoney(item.price.total, i18n.language)}
                    </div>
                    <div
                      className={`text-xs ${item.status === "registered" ? "text-good" : item.status === "failed" ? "text-danger" : "text-ink-3"}`}
                    >
                      {t(`order.${item.status}`)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="flex justify-between border-t border-rule pt-3 text-sm font-semibold">
              <span>{t("checkout.total")}</span>
              <span className="tabular-nums">{formatMoney(data.total, i18n.language)}</span>
            </div>
            <p className="text-xs text-ink-3">
              {t("order.placed", { date: format(data.createdAt) })}
              {data.paidAt
                ? ` · ${t("order.status.paid").split(",")[0]} ${format(data.paidAt)}`
                : ""}
            </p>
          </Card>
          {data.status === "completed" ? (
            <Link
              to="/admin/domains"
              className="text-sm text-accent underline-offset-2 hover:underline"
            >
              {t("order.manage")} →
            </Link>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
